import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { SalesOrderingPaymentProps } from '../../models/sales-ordering/sales-ordering-model';
import SalesOrderingPaymentMode from './sales-ordering-payment-mode';
import posService from '../../services/pos/pos.service';
import salesOrderingService from '../../services/sales-ordering/sales-ordering-service';

const SalesOrderingPayment: React.FC<SalesOrderingPaymentProps> = ({
    cart,
    totals,
    deliveryMethod,
    selectedUser,
    selectedAddress,
    isSelfPickup,
    onPaymentMethodChange,
    onPlaceOrder,
    onBack,
    isPlacingOrder = false,
    activePosId = null,
    selectedBatches = {},
    onBatchChange = () => {}
}) => {
    const [canProceed, setCanProceed] = useState(false);
    const [batchesMap, setBatchesMap] = useState<Record<number, any[]>>({});
    const [loadingBatches, setLoadingBatches] = useState<Record<number, boolean>>({});
    const [posOptions, setPosOptions] = useState<any[]>([]);

    // POS choices are labels only: the server resolves the logged-in account's assigned POS.
    useEffect(() => {
        let active = true;
        if (isSelfPickup) posService.getAllPOSBatch().then(response => {
            if (active) setPosOptions((response || []).filter((p: { posID: number }) => p.posID === activePosId)
                .map((p: { posID: number; posCode: string; address2?: string; cityName?: string }) => ({ label: `${p.posCode} - ${p.address2 || p.cityName || 'Outlet'}`, value: p.posID })));
        }).catch(() => { if (active) setPosOptions([]); });
        return () => { active = false; };
    }, [isSelfPickup, activePosId]);
    const batchChange = useRef(onBatchChange); batchChange.current = onBatchChange;
    const allocationsRef = useRef(selectedBatches); allocationsRef.current = selectedBatches;
    const batchKey = JSON.stringify(Array.from(cart.values()).map(i => [i.id, i.quantity]));
    useEffect(() => {
        let active = true;
        setBatchesMap({}); setLoadingBatches({});
        if (!isSelfPickup || !activePosId) return;
        Array.from(cart.values()).forEach(async item => {
            setLoadingBatches(prev => ({ ...prev, [item.id]: true }));
            try {
                const response = await salesOrderingService.getBatchesByPosAndProduct(activePosId, item.id);
                if (!active) return;
                const available = (response || []).filter((b: { availableStock: number }) => b.availableStock > 0);
                setBatchesMap(prev => ({ ...prev, [item.id]: available }));
                if (available.length && !allocationsRef.current[item.id]?.length)
                    batchChange.current(item.id, [{ batchId: available[0].batchId, quantity: Math.min(item.quantity, available[0].availableStock) }]);
            } catch {
                if (active) setBatchesMap(prev => ({ ...prev, [item.id]: [] }));
            } finally {
                if (active) setLoadingBatches(prev => ({ ...prev, [item.id]: false }));
            }
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelfPickup, activePosId, batchKey]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isHoveredRef = useRef(false);
    const scrollPosRef = useRef(0);
    const directionRef = useRef<'down' | 'up'>('down');
    const pauseTicksRef = useRef(0);

    // Reset scroll when cart items change
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        scrollPosRef.current = 0;
        directionRef.current = 'down';
        pauseTicksRef.current = 0;
    }, [cart]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const interval = setInterval(() => {
            if (isHoveredRef.current) {
                // If hovered, sync our internal float ref with actual scroll position
                // so when the mouse leaves, it resumes seamlessly from where the user manually scrolled.
                scrollPosRef.current = container.scrollTop;
                return;
            }

            const maxScroll = container.scrollHeight - container.clientHeight;
            if (maxScroll <= 1) {
                // Content fits fully, no need to scroll
                return;
            }

            if (pauseTicksRef.current > 0) {
                pauseTicksRef.current -= 1;
                return;
            }

            let nextPos = scrollPosRef.current;
            if (directionRef.current === 'down') {
                nextPos += 0.8;
                if (nextPos >= maxScroll) {
                    nextPos = maxScroll;
                    directionRef.current = 'up';
                    pauseTicksRef.current = 38; // ~1.5 seconds pause at 40ms interval
                }
            } else {
                nextPos -= 0.8;
                if (nextPos <= 0) {
                    nextPos = 0;
                    directionRef.current = 'down';
                    pauseTicksRef.current = 38; // ~1.5 seconds pause at 40ms interval
                }
            }

            scrollPosRef.current = nextPos;
            container.scrollTop = nextPos;
        }, 40);

        return () => clearInterval(interval);
    }, []);

    const handleAddAllocation = (productId: number) => {
        const item = cart.get(productId);
        if (!item) return;
        const itemBatches = batchesMap[productId] || [];
        const currentAllocations = selectedBatches[productId] || [];
        
        // Find first batch that is not already allocated
        const allocatedBatchIds = currentAllocations.map((a: any) => a.batchId);
        const nextBatch = itemBatches.find(b => !allocatedBatchIds.includes(b.batchId));
        if (!nextBatch) return;

        const totalAllocated = currentAllocations.reduce((sum: number, a: any) => sum + a.quantity, 0);
        const remaining = Math.max(0, item.quantity - totalAllocated);
        const quantityToAllocate = Math.min(remaining, nextBatch.availableStock);

        const newAllocations = [...currentAllocations, { batchId: nextBatch.batchId, quantity: quantityToAllocate }];
        onBatchChange(productId, newAllocations);
    };

    const handleRemoveAllocation = (productId: number, index: number) => {
        const currentAllocations = selectedBatches[productId] || [];
        const newAllocations = currentAllocations.filter((_: any, i: number) => i !== index);
        onBatchChange(productId, newAllocations);
    };

    const handleUpdateAllocation = (productId: number, index: number, field: 'batchId' | 'quantity', value: any) => {
        const currentAllocations = selectedBatches[productId] || [];
        const newAllocations = currentAllocations.map((a: any, i: number) => {
            if (i === index) {
                if (field === 'batchId') {
                    const nextBatch = (batchesMap[productId] || []).find((b: any) => b.batchId === value);
                    const item = cart.get(productId);
                    const totalAllocatedOther = currentAllocations.reduce((sum: number, alloc: any, idx: number) => idx === index ? sum : sum + alloc.quantity, 0);
                    const remaining = item ? Math.max(0, item.quantity - totalAllocatedOther) : 1;
                    const stock = nextBatch ? nextBatch.availableStock : 1;
                    return { batchId: value, quantity: Math.min(remaining, stock) };
                }
                return { ...a, [field]: value };
            }
            return a;
        });
        onBatchChange(productId, newAllocations);
    };

    const handleStepQuantity = (productId: number, index: number, delta: number) => {
        const currentAllocations = selectedBatches[productId] || [];
        const item = cart.get(productId);
        if (!item) return;

        const newAllocations = currentAllocations.map((a: any, i: number) => {
            if (i === index) {
                const nextBatch = (batchesMap[productId] || []).find((b: any) => b.batchId === a.batchId);
                const maxStock = nextBatch ? nextBatch.availableStock : Infinity;
                const totalAllocatedOther = currentAllocations.reduce((sum: number, alloc: any, idx: number) => idx === index ? sum : sum + alloc.quantity, 0);
                const maxAllowed = Math.max(0, item.quantity - totalAllocatedOther);

                const newQty = Math.max(1, Math.min(a.quantity + delta, Math.min(maxStock, maxAllowed)));
                return { ...a, quantity: newQty };
            }
            return a;
        });
        onBatchChange(productId, newAllocations);
    };

    const handleUpdateQuantityInput = (productId: number, index: number, valueStr: string) => {
        let numericVal = parseFloat(valueStr);
        if (isNaN(numericVal) || numericVal <= 0) numericVal = 1;

        const currentAllocations = selectedBatches[productId] || [];
        const item = cart.get(productId);
        if (!item) return;

        const newAllocations = currentAllocations.map((a: any, i: number) => {
            if (i === index) {
                const nextBatch = (batchesMap[productId] || []).find((b: any) => b.batchId === a.batchId);
                const maxStock = nextBatch ? nextBatch.availableStock : Infinity;
                const totalAllocatedOther = currentAllocations.reduce((sum: number, alloc: any, idx: number) => idx === index ? sum : sum + alloc.quantity, 0);
                const maxAllowed = Math.max(0, item.quantity - totalAllocatedOther);

                const newQty = Math.max(1, Math.min(numericVal, Math.min(maxStock, maxAllowed)));
                return { ...a, quantity: newQty };
            }
            return a;
        });
        onBatchChange(productId, newAllocations);
    };

    const [selectedPayment, setSelectedPayment] = useState<any>(null);

    useEffect(() => {
        if (!selectedPayment && totals.total > 0) {
            setCanProceed(false);
            return;
        }

        if (isSelfPickup) {
            const cartItems = Array.from(cart.values());
            const allBatchesAllocated = cartItems.every(item => {
                const allocations = selectedBatches[item.id] || [];
                if (allocations.length === 0) return false;

                // Make sure all allocations have selected batchId and quantity > 0
                const hasInvalidAllocation = allocations.some((a: any) => !a.batchId || a.quantity <= 0);
                if (hasInvalidAllocation) return false;

                // Make sure total allocated quantity matches the requested quantity exactly
                const totalAllocated = allocations.reduce((sum: number, a: any) => sum + a.quantity, 0);
                if (Math.abs(totalAllocated - item.quantity) > 0.000001) return false;

                // Make sure none of the allocations exceed available stock
                const itemBatches = batchesMap[item.id] || [];
                const exceedsStock = allocations.some((a: any) => {
                    const batch = itemBatches.find((b: any) => b.batchId === a.batchId);
                    return !batch || !Number.isFinite(a.quantity) || allocations.filter((other: { batchId: number }) => other.batchId === a.batchId).reduce((sum: number, other: { quantity: number }) => sum + other.quantity, 0) > batch.availableStock;
                });
                if (exceedsStock) return false;

                return true;
            });
            setCanProceed(!!activePosId && cartItems.length > 0 && allBatchesAllocated);
        } else {
            setCanProceed(true);
        }
    }, [isSelfPickup, cart, selectedBatches, selectedPayment, batchesMap, activePosId, totals.total]);

    const handlePaymentMode = useCallback((method: any) => {
        setSelectedPayment(method);
        onPaymentMethodChange(method);
    }, [onPaymentMethodChange]);

    return (
        <div className="sales-checkout-payment-container">
            {/* Header */}
            <div className="sales-payment-header">
                <Button
                    label="Back to Cart"
                    icon="pi pi-arrow-left"
                    className="p-button-text p-button-secondary p-0"
                    onClick={onBack}
                />
            </div>

            {/* Content */}
            <div className="sales-payment-content" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div className="grid m-0 flex-1" style={{ minHeight: 0 }}>

                    {/* Payment Section */}
                    <div className="col-12 md:col-7 sales-payment-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {isSelfPickup && (
                            <div className="p-4" style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                                <div className="font-bold text-xl mb-3 text-900 flex align-items-center" style={{ letterSpacing: '-0.3px', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                                    <i className="pi pi-tags text-green-600 mr-2 text-xl" />
                                    Batch Allocation
                                </div>

                                {posOptions.length > 1 ? (
                                    <div className="flex flex-column gap-1.5 mb-3 p-3 border-round" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <label className="font-semibold text-700 text-sm flex align-items-center mb-1">
                                            <i className="pi pi-map mr-1.5 text-green-600" />
                                            Point Of Sale (Active Outlet)
                                        </label>
                                        <Dropdown
                                            value={activePosId}
                                            options={posOptions}
                                            placeholder="Select a Point Of Sale..."
                                            className="w-full text-sm"
                                            filter
                                            disabled
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                ) : posOptions.length === 1 ? (
                                    <div className="mb-3 p-2.5 bg-green-50 text-green-800 font-semibold border-round border-1 border-green-200 text-xs flex align-items-center gap-2">
                                        <i className="pi pi-map-marker text-green-600 text-sm" />
                                        Point Of Sale: {posOptions[0].label}
                                    </div>
                                ) : <p className="text-600">Resolved POS: {activePosId ?? 'not available'} (your assigned POS)</p>}

                                <div className="flex flex-column gap-3">
                                    {Array.from(cart.values()).map((item) => {
                                        const itemBatches = batchesMap[item.id] || [];
                                        const isLoading = loadingBatches[item.id];
                                        const allocations = selectedBatches[item.id] || [];
                                        const totalAllocated = allocations.reduce((sum: number, a: any) => sum + (a.quantity || 0), 0);

                                        return (
                                            <div key={item.id} className="flex flex-column gap-1.5 pb-3 mb-2" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                <div className="flex justify-content-between align-items-center mb-2">
                                                    <span className="font-semibold text-800 text-sm flex align-items-center">
                                                        <i className="pi pi-box text-500 mr-1.5 text-xs" />
                                                        {item.name}
                                                    </span>
                                                    <span className="text-xs font-semibold text-600 bg-50 px-2 py-0.5 border-round border-1 border-200">
                                                        Req Qty: {item.quantity}
                                                    </span>
                                                </div>

                                                {/* Allocations Rows */}
                                                {!isLoading && allocations.map((alloc: any, idx: number) => {
                                                    const selectedBatch = itemBatches.find((b: any) => b.batchId === alloc.batchId);
                                                    const availableStock = selectedBatch ? selectedBatch.availableStock : 0;
                                                    const isStockExceeded = alloc.quantity > availableStock;

                                                    // Filter out already selected batches in other dropdowns
                                                    const otherSelectedBatchIds = allocations
                                                        .filter((_: any, i: number) => i !== idx)
                                                        .map((a: any) => a.batchId);
                                                    const availableOptions = itemBatches
                                                        .filter((b: any) => !otherSelectedBatchIds.includes(b.batchId))
                                                        .map((b: any) => ({
                                                            label: `Batch: ${b.batchCode} (Stock: ${b.availableStock})`,
                                                            value: b.batchId
                                                        }));

                                                    return (
                                                        <div key={idx} className="flex align-items-center gap-2 mb-2 w-full flex-wrap sm:flex-nowrap">
                                                            <Dropdown
                                                                value={alloc.batchId || null}
                                                                options={availableOptions}
                                                                onChange={(e) => handleUpdateAllocation(item.id, idx, 'batchId', e.value)}
                                                                placeholder="Select Batch..."
                                                                className="flex-1 text-sm min-w-0"
                                                                filter
                                                                emptyMessage="No available batches with stock found at this POS"
                                                                style={{ borderRadius: '8px', minWidth: '150px' }}
                                                            />
                                                            <div className="flex align-items-center gap-1 flex-shrink-0" style={{ background: '#f0fdf4', border: '2px solid #71d896ff', borderRadius: '8px', overflow: 'hidden', padding: '1px 3px' }}>
                                                                <Button
                                                                    icon="pi pi-minus"
                                                                    type="button"
                                                                    className="p-button-text p-button-success p-1"
                                                                    style={{ width: '24px', height: '24px', margin: 0, padding: 0 }}
                                                                    onClick={() => handleStepQuantity(item.id, idx, -1)}
                                                                    disabled={alloc.quantity <= 1}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="text-center font-bold text-green-800"
                                                                    style={{ width: '56px', border: 'none', background: 'transparent', outline: 'none', fontSize: '0.85rem' }}
                                                                    value={alloc.quantity}
                                                                    onChange={(e) => handleUpdateQuantityInput(item.id, idx, e.target.value)}
                                                                />
                                                                <Button
                                                                    icon="pi pi-plus"
                                                                    type="button"
                                                                    className="p-button-text p-button-success p-1"
                                                                    style={{ width: '24px', height: '24px', margin: 0, padding: 0 }}
                                                                    onClick={() => handleStepQuantity(item.id, idx, 1)}
                                                                    disabled={alloc.quantity >= availableStock || totalAllocated >= item.quantity}
                                                                />
                                                            </div>
                                                            {allocations.length > 1 && (
                                                                <Button
                                                                    icon="pi pi-times"
                                                                    severity="danger"
                                                                    className="p-button-text p-button-rounded flex-shrink-0"
                                                                    style={{ width: '28px', height: '28px', padding: 0, margin: 0 }}
                                                                    onClick={() => handleRemoveAllocation(item.id, idx)}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {isLoading && (
                                                    <div className="flex align-items-center gap-2 text-500 py-1">
                                                        <i className="pi pi-spin pi-spinner" />
                                                        <small>Loading available batches...</small>
                                                    </div>
                                                )}

                                                {/* Add Batch Button */}
                                                {!isLoading && itemBatches.length > allocations.length && totalAllocated < item.quantity && (
                                                    <Button
                                                        label="Add Batch"
                                                        icon="pi pi-plus"
                                                        className="p-button-text p-button-success p-1 text-xs font-bold mt-1 self-start"
                                                        onClick={() => handleAddAllocation(item.id)}
                                                        style={{ width: 'fit-content', padding: '0.25rem 0.5rem' }}
                                                    />
                                                )}

                                                {/* Unallocated and stock constraints validation messages */}
                                                {!isLoading && itemBatches.length === 0 && (
                                                    <small className="text-red-500 flex align-items-center gap-1 mt-1 font-medium">
                                                        <i className="pi pi-exclamation-triangle text-xs" />
                                                        No active stock batches found at this POS for this product.
                                                    </small>
                                                )}
                                                {!isLoading && totalAllocated < item.quantity && itemBatches.length > 0 && (
                                                    <small className="text-amber-700 flex align-items-center gap-1 mt-1 font-semibold" style={{ background: '#fffbeb', border: '1px solid #fef3c7', padding: '6px 10px', borderRadius: '6px', width: '100%' }}>
                                                        <i className="pi pi-info-circle text-xs text-amber-600 animate-pulse" />
                                                        Allocated: {totalAllocated} / {item.quantity} (Remaining: {item.quantity - totalAllocated}). Please allocate all quantities.
                                                    </small>
                                                )}
                                                {!isLoading && totalAllocated > item.quantity && (
                                                    <small className="text-red-500 flex align-items-center gap-1 mt-1 font-semibold" style={{ background: '#fef2f2', border: '1px solid #fee2e2', padding: '6px 10px', borderRadius: '6px', width: '100%' }}>
                                                        <i className="pi pi-exclamation-triangle text-xs text-red-600" />
                                                        Total allocated quantity ({totalAllocated}) exceeds requested quantity ({item.quantity}).
                                                    </small>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="p-4" style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                            <div className="font-bold text-xl mb-3 text-900 flex align-items-center" style={{ letterSpacing: '-0.3px', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                                <i className="pi pi-credit-card text-green-600 mr-2 text-xl" />
                                Select Payment Method
                            </div>

                            <SalesOrderingPaymentMode
                                paymentmode={handlePaymentMode}
                                isSelfPickup={isSelfPickup}
                                deliveryMethod={deliveryMethod}
                                selectedUserId={selectedUser?.userId}
                            />
                        </div>
                    </div>

                    {/* Order Summary Section */}
                    <div className="col-12 md:col-5 sales-payment-right-col" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        <div className="sales-summary-sticky flex-1" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div className="font-bold text-lg mb-3 text-900 pb-2 flex-shrink-0" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                Order Summary
                            </div>

                            <div 
                                ref={scrollContainerRef}
                                onMouseEnter={() => { isHoveredRef.current = true; }}
                                onMouseLeave={() => { isHoveredRef.current = false; }}
                                className="sales-summary-scrollable" 
                                style={{ padding: '0.5rem', background: '#fafafa', borderRadius: '8px', border: '1px solid #f1f5f9', overflowY: 'auto', flex: '1 1 auto', maxHeight: '180px', marginBottom: '0.75rem' }}
                            >
                                {/* Delivery Address */}
                                <div className="mb-3 p-3 border-round" style={{ background: '#ffffff', border: '1px dashed #cbd5e1', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                    <div className="text-xs font-semibold text-500 mb-2 uppercase" style={{ letterSpacing: '0.5px' }}>
                                        <i className="pi pi-map-marker mr-1" style={{ color: '#10b981' }} />
                                        Delivering To
                                    </div>
                                    {isSelfPickup && selectedUser ? (
                                        <div className="text-sm text-700 line-height-3">
                                            <div className="font-semibold text-900">{selectedUser.name}</div>
                                            <div className="font-semibold text-900">{selectedUser.mobile}</div>
                                            <div className="font-bold text-green-600 mt-1">Self Pickup</div>
                                        </div>
                                    ) : selectedAddress ? (
                                        <div className="text-sm text-700 line-height-3">
                                            <div className="font-semibold text-900">{selectedAddress.name}</div>
                                            <div>{selectedAddress.addressLine1}</div>
                                            {selectedAddress.addressLine2 && <div>{selectedAddress.addressLine2}</div>}
                                            <div>{selectedAddress.city}, {selectedAddress.pincode}</div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-red-500">No user/address selected</div>
                                    )}
                                </div>

                                {/* Items */}
                                <div className="mb-3">
                                    <div className="text-xs font-semibold text-600 mb-2 uppercase" style={{ letterSpacing: '0.5px' }}>
                                        <i className="pi pi-shopping-cart mr-1" style={{ color: '#10b981' }} />
                                        Order Items ({cart.size})
                                    </div>
                                    <div style={{ maxHeight: '120px', overflowY: 'auto' }} className="pr-1">
                                        {Array.from(cart.values()).map((item) => (
                                            <div key={item.id} className="flex justify-content-between text-sm mb-2" style={{ borderBottom: '1px dashed #f1f5f9', paddingBottom: '4px' }}>
                                                <div
                                                    className="flex-1 mr-2 text-700"
                                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    title={item.name}
                                                >
                                                    {item.quantity} × {item.name}
                                                </div>
                                                <div className="font-medium text-900 flex-shrink-0">
                                                    ₹{(item.unitPrice * item.quantity).toFixed(2)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Price Breakdown (Receipt Style) - Always Visible! */}
                            <div className="p-3" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: 'auto' }}>
                                <div className="sales-summary-row text-sm text-600 mb-2">
                                    <span>Subtotal</span>
                                    <span>₹{totals.subtotal.toFixed(2)}</span>
                                </div>
                                <div className="sales-summary-row text-sm text-600 mb-2">
                                    <span>Tax</span>
                                    <span>₹{totals.tax.toFixed(2)}</span>
                                </div>
                                {totals.delivery > 0 && (
                                    <div className="sales-summary-row text-sm text-600 mb-2">
                                        <span>Delivery Fee</span>
                                        <span>₹{totals.delivery.toFixed(2)}</span>
                                    </div>
                                )}
                                {(totals.rewardDiscount ?? 0) > 0 && (
                                    <div className="sales-summary-row text-sm text-green-600 mb-2">
                                        <span>Rewards Discount</span>
                                        <span>- ₹{(totals.rewardDiscount ?? 0).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="sales-summary-total-large mt-3 pt-3" style={{ borderTop: '2px dashed #cbd5e1' }}>
                                    <span>Total</span>
                                    <span style={{ color: '#10b981', fontWeight: 'bold' }}>₹{totals.total.toFixed(0)}</span>
                                </div>
                            </div>

                            {/* Desktop Button */}
                            <div className="pt-3 flex-shrink-0">
                                <Button
                                    label="Pay & Place Order"
                                    icon="pi pi-check-circle"
                                    className="p-button-success sales-desktop-action-btn w-full font-bold"
                                    disabled={!canProceed || isPlacingOrder}
                                    loading={isPlacingOrder}
                                    onClick={onPlaceOrder}
                                    size="large"
                                    style={{ marginTop: 0, marginBottom: 0 }}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Mobile Sticky Footer */}
            <div className="sales-mobile-sticky-footer md:hidden">
                <div className="flex flex-column">
                    <span className="sales-mobile-total-label uppercase text-xs font-semibold text-500 tracking-wide">Total to Pay</span>
                    <span className="sales-mobile-total-value font-bold text-xl" style={{ color: '#10b981' }}>₹{totals.total.toFixed(0)}</span>
                </div>
                <Button
                    label="Place Order"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="p-button-success font-bold px-4"
                    disabled={!canProceed || isPlacingOrder}
                    loading={isPlacingOrder}
                    onClick={onPlaceOrder}
                />
            </div>
        </div>
    );
};

export default SalesOrderingPayment;
